import { motion } from 'motion/react';
import { Container, Kicker, Mark } from '@/components/primitives';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FeatureGrid } from '@/components/FeatureGrid';
import { AIEngine } from '@/components/AIEngine';
import { ProductMockup } from '@/components/ProductMockup';
import { Workflow } from '@/components/Workflow';
import { Vision } from '@/components/Vision';
import { CTA } from '@/components/CTA';

const SURFACES = [
  {
    code: 'A',
    name: 'App di campo',
    who: 'Capocantiere · operai · capigruppo',
    desc: 'Il rapportino dal telefono: ore, mezzi, materiali, foto. Si salva da solo mentre scrivi, e in ufficio lo vedono subito.',
  },
  {
    code: 'B',
    name: 'Sala operativa',
    who: 'Geometri · computisti · responsabili tecnici',
    desc: "Una tabella per ogni cantiere, una vista per ogni agente AI. Audit trail integrato.",
  },
  {
    code: 'C',
    name: 'Quadro direzionale',
    who: 'Direzione tecnica · CFO · soci',
    desc: 'Tutti i cantieri in una pagina. Esposizione finanziaria, SAL in scadenza, rischi prioritari.',
  },
];

export default function ProdottoPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28">
        <div
          aria-hidden
          className="absolute inset-0 -z-10"
          style={{
            background:
              'radial-gradient(50% 50% at 50% -10%, color-mix(in oklch, var(--primary) 18%, transparent), transparent 70%)',
          }}
        />
        <Container className="relative">
          <Badge variant="soft">· Prodotto · BOSS 3.0</Badge>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="boss-display mt-6 max-w-[18ch] text-balance text-[56px] md:text-[104px]"
          >
            Tre superfici. <br /> Un solo <Mark>cantiere.</Mark>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.9 }}
            className="mt-7 max-w-2xl text-pretty text-[18px] leading-[1.55] text-fg-2"
          >
            BOSS è il gestionale della commessa edile: una sola piattaforma con tre porte d'ingresso, una per
            ogni ruolo. Stessa fonte di verità, tre interfacce calibrate per chi le userà davvero.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <Button asChild size="lg">
              <a href="#demo">Prenota una demo →</a>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="#funzionalita">Le funzionalità ↓</a>
            </Button>
          </motion.div>
        </Container>
      </section>

      <FeatureGrid index="01" />

      {/* Surfaces grid */}
      <section className="relative border-t border-border py-24 md:py-32">
        <Container>
          <Kicker index="02" label="Le tre superfici" />
          <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-3">
            {SURFACES.map((s, i) => (
              <motion.div
                key={s.code}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: i * 0.1, duration: 0.7 }}
                className="bg-background p-8"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary">
                  Superficie · {s.code}
                </div>
                <h3 className="boss-h1 mt-4 text-[32px]">{s.name}</h3>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {s.who}
                </div>
                <p className="mt-5 text-pretty text-[14px] leading-[1.6] text-fg-2">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Product mockup */}
      <section className="relative border-t border-border py-24 md:py-32">
        <Container>
          <Kicker index="03" label="Sala operativa · anteprima" />
          <h2 className="boss-display mt-5 max-w-3xl text-balance text-[40px] md:text-[64px]">
            Lo schermo che un <Mark>direttore tecnico</Mark> apre alle 7:30.
          </h2>
          <div className="mt-16">
            <ProductMockup />
          </div>
        </Container>
      </section>

      <AIEngine index="04" />
      <Workflow index="05" />
      <Vision index="06" />
      <CTA />
    </>
  );
}
